<template>
    <div class="w-full">
        <div
            v-if="itemsCurrent.length && enabled"
            class="flex items-center gap-2 mb-3"
        >
            <span class="text-xs text-theme-info">
                {{ itemsCurrent.length }} imagen{{
                    itemsCurrent.length !== 1 ? "es" : ""
                }}
            </span>
            <span class="text-xs text-theme-info"
                >· Arrastra para reordenar</span
            >
        </div>

        <div class="flex flex-wrap items-start gap-6 pt-3">
            <Container
                v-if="itemsCurrent.length"
                orientation="horizontal"
                group-name="images"
                :get-child-payload="getImagePayload"
                drag-class="image-dragging"
                drop-class="image-drop-zone"
                :drop-placeholder="$data.$dropPlaceholderOptions"
                class="images-dnd-container flex flex-wrap gap-6"
                @drag-start="isDragging = true"
                @drag-end="isDragging = false"
                @drop="onDropImage"
            >
                <Draggable v-for="(item, index) in itemsCurrent" :key="item.id">
                    <div
                        class="image-card relative"
                        :class="{ 'is-dragging-active': isDragging }"
                    >
                        <div
                            class="image-frame w-30 h-30 rounded-xl overflow-hidden border-2 border-brand-blue bg-theme-input shadow-sm"
                        >
                            <img
                                v-img:group
                                v-img-loading
                                loading="lazy"
                                :src="item.url | image"
                                :alt="item.alt"
                                width="120"
                                height="120"
                                class="select-none object-cover w-full h-full"
                            />
                        </div>

                        <div
                            v-if="enabled"
                            class="drag-handle absolute flex flex-col items-center justify-center rounded-xl opacity-0 transition-opacity duration-200 w-30 h-30"
                        >
                            <span class="drag-handle__icon">⠿</span>
                            <span class="text-white text-xs mt-1 font-medium"
                                >Mover</span
                            >
                        </div>

                        <a
                            class="absolute -top-2.5 -right-2.5 bg-brand-blue text-white h-5 rounded-full px-1.5 flex items-center justify-center shadow-md hover:bg-stack-primary transition-colors whitespace-nowrap font-semibold"
                            style="font-size: 9px"
                            target="_blank"
                            rel="noopener"
                            :href="`/app/images/detail/${item.id}`"
                        >
                            #{{ item.id }}
                        </a>

                        <div
                            v-if="index === 0"
                            class="absolute -top-2.5 right-1/2 -translate-x-1/2 bg-stack-primary text-white rounded-full px-2 py-0.5 whitespace-nowrap"
                            style="font-size: 10px"
                        >
                            Principal
                        </div>

                        <button
                            v-if="enabled"
                            class="absolute -bottom-2.5 -right-2.5 w-6 h-6 rounded-full bg-theme-danger text-white flex items-center justify-center shadow-md hover:scale-110 transition-transform"
                            @click="deleteImage(index)"
                        >
                            <trash-icon class="w-3 h-3" />
                        </button>
                    </div>
                </Draggable>
            </Container>
            <button
                v-if="directUpload && !itemsCurrent.length && enabled"
                class="w-full h-28 rounded-xl border-2 border-dashed border-brand-blue flex flex-col items-center justify-center gap-1.5 text-theme-muted hover:border-stack-primary hover:text-stack-primary transition-colors cursor-pointer"
                :disabled="isDirectUploading"
                @click="triggerDirectUpload"
            >
                <photo-icon class="w-8 h-8" />
                <span class="text-sm font-medium">Agregar fotos</span>
                <span class="text-xs">Puedes seleccionar varias</span>
            </button>

            <custom-tooltip
                v-else-if="
                    enabled && (directUpload || $enabledEcommerceOptions)
                "
                class="self-center"
                border
            >
                <button
                    class="image-add-btn rounded-xl border-2 border-dashed border-brand-blue flex flex-col items-center justify-center gap-1.5 w-30 h-30 text-theme-muted hover:border-stack-primary hover:text-stack-primary transition-colors"
                    :disabled="isDirectUploading"
                    @click="handleAddClick"
                >
                    <photo-icon class="w-20 h-20" />
                    <span class="text-xs font-medium">Agregar</span>
                </button>
                <template #tooltip>Agregar imágenes</template>
            </custom-tooltip>
        </div>

        <input
            v-if="directUpload"
            ref="directFileInput"
            type="file"
            multiple
            accept=".jpg,.jpeg,.png,.webp"
            class="hidden"
            @input="onDirectFilesSelected"
        />
        <image-rights-notice
            v-if="directUpload && enabled && !hideImageRightsNotice"
            class="mt-2"
        />
        <p v-if="isDirectUploading" class="text-theme-info text-sm mt-2">
            Subiendo {{ directUploadedCount }} de {{ directTotalCount }}...
        </p>
        <div>
            <app-dialog v-model="selectImage" size="lg" :persistent="isLoading">
                <template #header>
                    <div class="flex items-center justify-between gap-4">
                        <h3
                            v-show="!isOpenImageCreator"
                            class="text-lg font-semibold text-theme-base"
                        >
                            Selecciona las imagenes
                        </h3>
                        <h3
                            v-show="isOpenImageCreator"
                            class="text-lg font-semibold text-theme-base"
                        >
                            Cargar nueva imagen
                        </h3>

                        <div class="flex items-center gap-2">
                            <app-button
                                v-show="
                                    !isOpenImageCreator && selectedImages.length
                                "
                                @click="pushNewImage"
                            >
                                Confirmar ({{ selectedImages.length }})
                            </app-button>
                            <custom-tooltip
                                v-if="$ability.hasPermission('image:Create')"
                                border
                                bottom
                            >
                                <app-button
                                    circle
                                    icon
                                    @click="
                                        isOpenImageCreator = !isOpenImageCreator
                                    "
                                >
                                    <chevron-left-icon
                                        v-if="isOpenImageCreator"
                                        class="h-4 w-4"
                                    />
                                    <span v-else>➕</span>
                                </app-button>
                                <template #tooltip>
                                    <span>
                                        {{
                                            isOpenImageCreator
                                                ? "Lista de imágenes"
                                                : "Cargar nueva imagen"
                                        }}
                                    </span>
                                </template>
                            </custom-tooltip>
                        </div>
                    </div>
                </template>
                <div>
                    <div v-if="isOpenImageCreator">
                        <image-creator
                            :default-alt="defaultAlt"
                            :default-size="defaultSize"
                            :allow-edit-alt="true"
                            :hide-image-rights-notice="hideImageRightsNotice"
                            @loading="onLoading"
                            @created="selectionUploadImage"
                        />
                    </div>
                    <div v-else>
                        <div
                            class="flex justify-between flex-col lg:flex-row w-full"
                        >
                            <div class="my-3 flex flex-col gap-1">
                                <div class="flex items-center">
                                    <custom-input
                                        v-model="keyword"
                                        type="text"
                                        placeholder="Buscar .."
                                        class="mr-2"
                                        @click-icon="advanceSearch"
                                        @enter="advanceSearch"
                                    >
                                        <template #icon> 🔎 </template>
                                    </custom-input>
                                    <app-button
                                        icon
                                        circle
                                        color="brand-purple"
                                        @click="reloadItems(true)"
                                    >
                                        <reload-icon
                                            class="h-5 w-5 text-white"
                                        />
                                    </app-button>
                                </div>

                                <button
                                    type="button"
                                    class="self-start text-xs text-theme-muted hover:underline"
                                    @click="toggleSharedBank"
                                >
                                    {{
                                        viewingSharedBank
                                            ? "← Volver a mis imágenes"
                                            : "Ver banco de imágenes compartidas"
                                    }}
                                </button>
                            </div>

                            <custom-pagination
                                v-model="currentPage"
                                :length="pagesNumber"
                                @input="listItems()"
                            />
                        </div>
                        <div
                            v-if="imagesList.length"
                            class="w-full mt-2 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3"
                        >
                            <div
                                v-for="(item, index) in imagesList"
                                :key="index"
                            >
                                <button
                                    class="relative w-full rounded-xl overflow-hidden border-2 transition-all block"
                                    :class="
                                        selectedImages.includes(item.id)
                                            ? 'border-stack-primary shadow-lg scale-95'
                                            : 'border-transparent hover:border-stack-primary/50'
                                    "
                                    @click="selectionImage(item.id)"
                                >
                                    <img
                                        class="w-full aspect-square object-cover"
                                        loading="lazy"
                                        :src="item.previewUrl || item.url"
                                        :alt="item.alt"
                                    />
                                    <div
                                        class="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-2 flex items-end justify-between gap-1"
                                    >
                                        <div
                                            class="bg-brand-red rounded-lg py-1 px-0.5 border border-white"
                                        >
                                            <p
                                                class="text-white text-xs truncate flex-1 text-left"
                                            >
                                                {{ item.alt || "Sin nombre" }}
                                            </p>
                                        </div>

                                        <a
                                            :href="`/app/images/detail/${item.id}`"
                                            target="_blank"
                                            rel="noopener"
                                            class="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center transition-colors"
                                            @click.stop
                                        >
                                            <pencil-icon
                                                class="w-3 h-3 text-white"
                                            />
                                        </a>
                                    </div>
                                    <div
                                        v-if="selectedImages.includes(item.id)"
                                        class="absolute top-1.5 right-1.5 w-6 h-6 bg-stack-primary rounded-full flex items-center justify-center shadow-md"
                                    >
                                        <check-icon
                                            class="w-3.5 h-3.5 text-white"
                                        />
                                    </div>
                                </button>
                            </div>
                        </div>
                        <div v-else class="w-11/12 mx-auto mt-4">
                            <strong>
                                Nuestro sistema NO encuentra resultados. Intenta
                                modificar los filtros de búsqueda.
                            </strong>
                        </div>
                    </div>
                </div>
            </app-dialog>
        </div>
    </div>
</template>

<script>
import { Container, Draggable } from "vue-smooth-dnd";
import { mapActions, mapGetters, mapState } from "vuex";

import { sizesImages } from "@/common/fields/sizes-images";
import { applyDrag } from "@/common/lib/drag";
import {
    aspectErrorMessage,
    validateAspectRatio
} from "@/common/lib/image-dimensions";
import {
    SVG_NOT_ALLOWED_MESSAGE,
    isAllowedImageFile
} from "@/common/lib/image-files";

import CheckIcon from "@/components/icons/CheckIcon.vue";
import ChevronLeftIcon from "@/components/icons/ChevronLeftIcon.vue";
import PencilIcon from "@/components/icons/PencilIcon.vue";
import PhotoIcon from "@/components/icons/PhotoIcon.vue";
import ReloadIcon from "@/components/icons/ReloadIcon.vue";
import TrashIcon from "@/components/icons/TrashIcon.vue";
import AppDialog from "@/components/utils/AppDialog.vue";
import CustomInput from "@/components/utils/CustomInput.vue";
import ImageCreator from "@/components/utils/ImageCreator.vue";
import ImageRightsNotice from "@/components/utils/ImageRightsNotice.vue";

export default {
    name: "ImagesManage",
    components: {
        AppDialog,
        CheckIcon,
        ChevronLeftIcon,
        Container,
        CustomInput,
        Draggable,
        ImageCreator,
        ImageRightsNotice,
        PencilIcon,
        PhotoIcon,
        ReloadIcon,
        TrashIcon
    },
    model: {
        prop: "itemsCurrent",
        event: "change"
    },
    props: {
        itemsCurrent: {
            type: Array,
            default: () => [],
            required: true
        },
        id: {
            type: String,
            default: () => "",
            required: false
        },
        message: {
            type: String,
            default: () => "referencia",
            required: false
        },
        defaultAlt: {
            type: String,
            default: () => ""
        },
        defaultSize: {
            type: String,
            default: () => ""
        },
        enabled: {
            default: () => true,
            type: Boolean,
            required: false
        },
        directUpload: {
            type: Boolean,
            default: () => false
        },
        defaultFilters: {
            type: Object,
            default: () => ({}),
            required: false
        },
        hideImageRightsNotice: {
            type: Boolean,
            default: false
        }
    },
    emits: ["change"],
    data: () => ({
        $dropPlaceholderOptions: {
            className: "drop-preview",
            animationDuration: "150",
            showOnTop: true
        },
        isDragging: false,
        selectImage: false,
        selectedImages: [],
        isLoading: false,
        currentPage: 1,
        pagesNumber: 0,
        isOpenImageCreator: false,
        imagesList: [],

        itemsNumber: 0,
        backup: {
            items: [],
            pagination: { pagesNumber: 1, itemsNumber: 1 }
        },
        keyword: "",
        viewingSharedBank: false,
        isDirectUploading: false,
        directUploadedCount: 0,
        directTotalCount: 0
    }),
    computed: {
        ...mapState("images", ["images"]),
        ...mapGetters("control", ["backgroundColor", "borderCardDark"])
    },
    watch: {
        keyword(value) {
            if (value) {
                this.imagesList = this.images.filter((item) =>
                    item.alt.includes(value.toLowerCase())
                );
                this.itemsNumber = this.imagesList.length;
                this.pagesNumber = 1;
                this.currentPage = 1;
            } else {
                this.imagesList = [...this.backup.items];
                this.itemsNumber = Number(this.backup.pagination.itemsNumber);
                this.pagesNumber = Number(this.backup.pagination.pagesNumber);
            }
        }
    },
    mounted() {},
    methods: {
        ...mapActions("images", [
            "listImagesPaginated",
            "listSharedImagesPaginated",
            "getEndPointUploadImage",
            "uploadImage",
            "createImage"
        ]),
        handleAddClick() {
            if (this.directUpload) {
                this.triggerDirectUpload();
            } else {
                this.openNewImage();
            }
        },
        triggerDirectUpload() {
            this.$refs.directFileInput.value = "";
            this.$refs.directFileInput.click();
        },
        async onDirectFilesSelected(event) {
            const files = Array.from(
                event.target.files || event.dataTransfer.files
            );
            if (!files.length) return;

            this.isDirectUploading = true;
            this.directUploadedCount = 0;
            this.directTotalCount = files.length;

            const newImages = [];

            for (const file of files) {
                if (!isAllowedImageFile(file)) {
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: "Formato no permitido",
                        text: `${file.name}: ${SVG_NOT_ALLOWED_MESSAGE}`,
                        icon: "warning"
                    });
                    this.directUploadedCount++;
                    continue;
                }
                const fileSizeMB = file.size / 1048576;
                if (fileSizeMB > 20) {
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: "Archivo muy pesado",
                        text: `${file.name} supera los 20 MB`,
                        icon: "warning"
                    });
                    this.directUploadedCount++;
                    continue;
                }

                const uploadSize = this.defaultSize || "squareImage";
                const sizeSpec = sizesImages.find(
                    (item) => item.size === uploadSize
                );
                if (sizeSpec) {
                    const aspect = await validateAspectRatio(file, sizeSpec);
                    if (!aspect.valid) {
                        this.$swal.fire({
                            background: this.backgroundColor,
                            title: "Proporción no recomendada",
                            text: `${file.name}: ${aspectErrorMessage(aspect)}`,
                            icon: "warning"
                        });
                    }
                }

                try {
                    const previewUrl = URL.createObjectURL(file);
                    const { id, url, urlSigned } =
                        await this.getEndPointUploadImage({
                            fileName: file.name,
                            fileSize: file.size,
                            alt: this.defaultAlt || "producto",
                            size: uploadSize
                        });
                    await this.uploadImage({ url: urlSigned, file });
                    // createImage siempre cierra el cargue: el backend completa
                    // el alt faltante y marca la imagen como cargada con éxito
                    const { alt } = await this.createImage({
                        id,
                        url,
                        alt: this.defaultAlt || "producto",
                        size: uploadSize
                    });

                    newImages.push({
                        id,
                        alt,
                        url,
                        previewUrl,
                        sequence: {
                            sequence:
                                this.itemsCurrent.length + newImages.length
                        }
                    });
                } catch (error) {
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: "Error al subir",
                        text: error.message || `Error con ${file.name}`,
                        icon: "error"
                    });
                } finally {
                    this.directUploadedCount++;
                }
            }

            if (newImages.length) {
                this.$emit("change", [...this.itemsCurrent, ...newImages]);
            }

            this.isDirectUploading = false;
        },
        async openNewImage() {
            this.selectImage = true;
            this.listItems(true);
        },
        async reloadItems() {
            this.keyword = "";
            this.currentPage = 1;
            await this.listItems(true);
        },
        async advanceSearch() {
            this.currentPage = 1;
            await this.listItems(true);
        },
        async toggleSharedBank() {
            this.viewingSharedBank = !this.viewingSharedBank;
            this.keyword = "";
            this.currentPage = 1;
            await this.listItems(true);
        },
        async listItems(complete = false) {
            this.isLoading = true;
            try {
                const { items, pagination } = this.viewingSharedBank
                    ? await this.listSharedImagesPaginated({
                          page: this.currentPage,
                          keyword: this.keyword,
                          complete
                      })
                    : await this.listImagesPaginated({
                          ...this.defaultFilters,
                          page: this.currentPage,
                          keyword: this.keyword,
                          complete
                      });
                this.imagesList = items;
                if (pagination) {
                    this.itemsNumber = pagination.itemsNumber;
                    this.pagesNumber = pagination.pagesNumber;
                }
                this.backup = {
                    items,
                    pagination: {
                        itemsNumber: Number(this.itemsNumber),
                        pagesNumber: Number(this.pagesNumber)
                    }
                };
                this.isLoading = false;
            } catch (error) {
                this.isLoading = false;
                this.selectImage = false;
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        getImagePayload(index) {
            return this.itemsCurrent[index];
        },
        onDropImage(dropResult) {
            if (
                dropResult.removedIndex !== null ||
                dropResult.addedIndex !== null
            ) {
                const reordered = applyDrag(
                    [...this.itemsCurrent],
                    dropResult
                ).map((item, index) => ({
                    ...item,
                    sequence: { sequence: index }
                }));
                this.$emit("change", reordered);
            }
        },
        moveOrder(fromIndex, toIndex) {
            let arrAux = [...this.itemsCurrent];
            const aux = arrAux[fromIndex];
            arrAux.splice(fromIndex, 1);
            arrAux.splice(toIndex, 0, aux);
            const imagesResult = arrAux.map((item, index) => ({
                ...item,
                sequence: { sequence: index }
            }));
            this.$emit("change", imagesResult);
        },
        selectionImage(id) {
            let fromIndex = this.selectedImages.findIndex(
                (item) => item === id
            );
            if (fromIndex === -1) {
                this.selectedImages.push(id);
            } else {
                this.selectedImages.splice(fromIndex, 1);
                let arrAux = [...this.itemsCurrent];
                fromIndex = arrAux.findIndex((item) => item === id);
                if (fromIndex !== -1) {
                    arrAux.splice(fromIndex, 1);
                    this.$emit("change", arrAux);
                }
            }
        },
        pushNewImage() {
            let imagesAux = this.imagesList.filter((i) =>
                this.selectedImages.includes(i.id)
            );
            if (imagesAux.length) {
                const imagesResult = [...this.itemsCurrent];

                imagesAux = imagesAux.map((item, index) => ({
                    ...item,
                    sequence: { sequence: this.itemsCurrent.length + index }
                }));
                imagesAux = [...imagesResult, ...imagesAux];

                imagesAux = imagesAux.filter(
                    (value, index, self) =>
                        self.findIndex((item) => item.id === value.id) === index
                );
                this.selectImage = false;
                this.$emit("change", imagesAux);
            }
        },
        deleteImage(fromIndex) {
            let imagesResult = [...this.itemsCurrent];
            imagesResult.splice(fromIndex, 1);
            this.$emit("change", imagesResult);
        },
        selectionUploadImage({ alt, id, previewUrl, size, url }) {
            this.imagesList.unshift({ alt, size, id, url, previewUrl });
            this.selectedImages.unshift(id);
            this.isOpenImageCreator = false;
        },
        onLoading(value) {
            this.isLoading = value;
        }
    }
};
</script>

<style scoped>
.image-card {
    cursor: grab;
    padding: 0.625rem;
    transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.image-card:hover {
    transform: scale(1.04);
}

.image-card:hover .image-frame {
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.22);
}

.image-card:hover .drag-handle {
    opacity: 1;
}

.image-card.is-dragging-active .image-frame {
    outline: 2px dashed #a0aec0;
    outline-offset: 2px;
}

.drag-handle {
    background: rgba(0, 0, 0, 0.48);
    cursor: grab;
    pointer-events: none;
    top: 0.625rem;
    left: 0.625rem;
}

.drag-handle__icon {
    font-size: 2rem;
    color: #fff;
    line-height: 1;
    user-select: none;
    text-shadow: 0 1px 4px rgba(0, 0, 0, 0.6);
}

.images-dnd-container {
    width: fit-content !important;
    min-width: 0 !important;
}

.image-dragging {
    opacity: 0.35;
    transform: scale(1.08) rotate(2deg);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.4);
    border-radius: 0.75rem;
}

.drop-preview {
    width: 120px;
    height: 120px;
    background: rgba(99, 179, 237, 0.12);
    border: 2px dashed #63b3ed;
    border-radius: 0.75rem;
    margin: 0.625rem;
}
</style>
